'use client'

import { useAuthorization } from '@entities/authentication'
import { subscribesTicketEntityQueries } from '@entities/subscribes/api'
import {
  SubscribeTicketCard,
  SubscribeTicketLanguageChip,
  SubscribeTicketLessonCountChip,
} from '@entities/subscribes/ui'
import { langTypeSchema, lessonCountPerMonthSchema, lessonTimeSchema, paymentTypeSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { transformNumberEnumToStringEnum } from '@shared/libs'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useRouter, useSearchParams } from 'next/navigation'
import { match } from 'ts-pattern'
import z from 'zod'

import { Typography, VStack } from '@podo-app/design-system-temp'

import { LECTURE_TICKETS_QUERY_PARAMS } from '../../model'

type Props = {
  langType: z.infer<typeof langTypeSchema>
  lessonTime: z.infer<typeof lessonTimeSchema>
  lessonCountPerMonth: z.infer<typeof lessonCountPerMonthSchema>
}

export const SubscribeLectureTickets = ({ langType, lessonTime, lessonCountPerMonth }: Props) => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const searchParams = useSearchParams()
  const router = useRouter()

  const { data: subscribeTicketsData } = useSuspenseQuery({
    ...subscribesTicketEntityQueries.subscribeList({
      bearerToken,
      langType: langType,
      lessonTime: lessonTime.toString() as NumberEnumToStringEnum<z.infer<typeof lessonTimeSchema>>,
      lessonCountPerMonth: lessonCountPerMonth.toString() as NumberEnumToStringEnum<
        z.infer<typeof lessonCountPerMonthSchema>
      >,
    }),
  })

  const subscribeTickets = Array.isArray(subscribeTicketsData) ? subscribeTicketsData : []

  return (
    <VStack className="gap-8">
      <VStack className="gap-2">
        <Typography size="h1" className="text-[28px] font-bold">
          {t('subscribeList.paymentTypeHero.subscribeTitle')}
        </Typography>
        <Typography size="h4" className="text-[18px]">
          {t('subscribeList.paymentTypeHero.subscribeLine1')}
          <br />
          {t('subscribeList.paymentTypeHero.subscribeLine2')}
        </Typography>
      </VStack>

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.language')}</Typography>

        <SubscribeTicketLanguageChip
          selectedLanguage={langType}
          handleChangeLanguage={(selectedLangType) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.langType, selectedLangType)

            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
        />
      </VStack>

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.lessonCount')}</Typography>

        <SubscribeTicketLessonCountChip
          selectedLessonCount={transformNumberEnumToStringEnum(lessonCountPerMonth)}
          handleChangeLessonCount={(selectedLessonCount) => {
            const queryParams = new URLSearchParams(searchParams.toString())

            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonCountPerMonth, selectedLessonCount)
            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
          paymentType={paymentTypeSchema.enum.SUBSCRIBE}
        />
      </VStack>

      {/* NOTE: 레슨 시간 추가 시 활성화 */}
      {/* <VStack className="gap-4">
        <Typography size="h3">레슨 시간을 선택하세요.</Typography>

        <SubscribeTicketLessonTimeChip
          selectedLessonTime={transformNumberEnumToStringEnum(lessonTime)}
          handleChangeLessonTime={(selectedLessonTime) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonTime, selectedLessonTime)
            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
        />
      </VStack> */}

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.product')}</Typography>

        {subscribeTickets.map((subscribeTicket) => (
          <SubscribeTicketCard
            month={subscribeTicket.lessonMonth}
            key={subscribeTicket.id}
            subscribeTicketId={subscribeTicket.id}
            catalogTicket={subscribeTicket}
            price={subscribeTicket.originPrice}
            descriptions={match(transformNumberEnumToStringEnum(lessonCountPerMonth))
              .with('999', () => [
                t('subscribeList.ticketDescriptions.dailyFast'),
                t('subscribeList.ticketDescriptions.materials'),
              ])
              .with('8', () => [
                `${t('subscribeList.ticketDescriptions.flexible')} (${t('subscribeList.chip.minutes', { count: 25 })})`,
                t('subscribeList.ticketDescriptions.materials'),
              ])
              .otherwise(() => [])}
            isBest={subscribeTicket.isBest}
          />
        ))}
      </VStack>
    </VStack>
  )
}
